import { z } from 'zod';
export const sessionStages = [
    'entering',
    'discovery',
    'organizing',
    'authenticity_check',
    'allocating',
    'reflecting',
    'completed',
    'returning',
];
export const SessionStageSchema = z.enum(sessionStages);
export const MeaningStatusSchema = z.enum(['hypothesis', 'core', 'archived', 'popped']);
export const ShellSchema = z.object({
    id: z.uuid(),
    meaningId: z.uuid(),
    title: z.string().min(1).max(80),
    strength: z.number().int().min(0).max(100),
    confidence: z.number().int().min(0).max(100),
    status: z.enum(['proposed', 'confirmed', 'removed']),
});
export const MeaningSchema = z.object({
    id: z.uuid(),
    title: z.string().min(1).max(48),
    importance: z.number().int().min(0).max(100),
    realization: z.number().int().min(0).max(100),
    vitality: z.number().int().min(0).max(100),
    authenticity: z.number().int().min(0).max(100),
    externalPressure: z.number().int().min(0).max(100),
    confidence: z.number().int().min(0).max(100),
    status: MeaningStatusSchema,
    publicVisible: z.boolean(),
    x: z.number().min(-1).max(1),
    y: z.number().min(-1).max(1),
    shell: ShellSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
const baseOperation = {
    meaning_id: z.string().min(1).max(64),
    confidence: z.number().min(0).max(1),
    reason: z.string().max(400),
};
export const AiOperationSchema = z.discriminatedUnion('type', [
    z.object({ ...baseOperation, type: z.literal('CREATE_MEANING'), title: z.string().min(1).max(48) }),
    z.object({ ...baseOperation, type: z.literal('UPDATE_MEANING'), title: z.string().min(1).max(48), vitality: z.number().int().min(0).max(100), realization: z.number().int().min(0).max(100) }),
    z.object({ ...baseOperation, type: z.literal('PROPOSE_RENAME'), title: z.string().min(1).max(48) }),
    z.object({ ...baseOperation, type: z.literal('PROPOSE_MERGE'), target_meaning_id: z.string().min(1).max(64), title: z.string().min(1).max(48) }),
    z.object({ ...baseOperation, type: z.literal('PROPOSE_SPLIT'), titles: z.array(z.string().min(1).max(48)).length(2) }),
    z.object({ ...baseOperation, type: z.literal('PROPOSE_SHELL'), title: z.string().min(1).max(80), strength: z.number().int().min(0).max(100) }),
    z.object({ ...baseOperation, type: z.literal('PROPOSE_POP') }),
    z.object({ ...baseOperation, type: z.literal('ADD_GOAL'), title: z.string().min(1).max(120) }),
    z.object({ ...baseOperation, type: z.literal('UPDATE_ENERGY'), vitality: z.number().int().min(0).max(100) }),
    z.object({ ...baseOperation, type: z.literal('UPDATE_REALIZATION'), realization: z.number().int().min(0).max(100) }),
    z.object({ ...baseOperation, type: z.literal('NO_CHANGE') }),
]);
export const AiTurnSchema = z.object({
    assistant_message: z.string().min(1).max(1400),
    operations: z.array(AiOperationSchema).max(4),
    next_focus: z.string().min(1).max(240),
    session_stage: SessionStageSchema,
});
export const ReflectionSessionSchema = z.object({
    id: z.uuid(),
    stage: SessionStageSchema,
    startedAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().nullable(),
});
export const PreferencesSchema = z.object({
    publicMeanings: z.boolean(),
    ghostConsent: z.boolean(),
    sound: z.boolean(),
    ambient: z.boolean(),
    haptics: z.boolean(),
});
export const PublicActorSchema = z.object({
    publicId: z.uuid(),
    kind: z.enum(['live', 'ghost']),
    x: z.number().min(-1).max(1),
    y: z.number().min(-1).max(1),
    meanings: z.array(z.object({
        id: z.string().max(64),
        publicCategory: z.string().min(1).max(48).nullable(),
        importance: z.number().int().min(0).max(100),
    })).max(12),
});
export const GhostEventSchema = z.discriminatedUnion('event', [
    z.object({ t: z.number().min(0).max(900), event: z.literal('MOVE'), x: z.number().min(-1).max(1), y: z.number().min(-1).max(1) }),
    z.object({ t: z.number().min(0).max(900), event: z.literal('CREATE_BUBBLE'), bubbleId: z.string().max(64), publicCategory: z.string().max(48).nullable() }),
    z.object({ t: z.number().min(0).max(900), event: z.literal('RESIZE_BUBBLE'), bubbleId: z.string().max(64), value: z.number().int().min(0).max(100) }),
    z.object({ t: z.number().min(0).max(900), event: z.enum(['MERGE_BUBBLE', 'SPLIT_BUBBLE', 'POP_BUBBLE']), bubbleId: z.string().max(64) }),
]);
export const GhostTrackSchema = z.object({
    publicId: z.uuid(),
    events: z.array(GhostEventSchema).max(600),
});
export const MapSnapshotSchema = z.object({
    session: ReflectionSessionSchema,
    meanings: z.array(MeaningSchema),
    preferences: PreferencesSchema,
    releasedEnergy: z.number().int().min(0).max(100),
});
export const AiMessageResponseSchema = z.object({
    message: z.string(),
    operations: z.array(AiOperationSchema),
    map: MapSnapshotSchema,
    safety: z.boolean().default(false),
});
export const AnalyticsEventSchema = z.enum([
    'app_opened', 'world_entered', 'first_answer_sent', 'first_meaning_created',
    'meaning_created', 'meaning_resized', 'meaning_merged', 'meaning_split',
    'shell_detected', 'shell_removed', 'meaning_popped', 'life_points_allocated',
    'ghost_seen', 'ghost_tapped', 'session_completed', 'session_resumed',
    'sound_disabled', 'haptics_disabled',
]);
//# sourceMappingURL=contracts.js.map